import { getBrandingLogoSrc } from "../core/theme/branding-logo-source";

const TEMPLATE_LOGO_PATH = "assets/logo.png";

let brandingLogoDataUriPromise: Promise<string | null> | null = null;

export function resetBrandingLogoDataUriCache(): void {
  brandingLogoDataUriPromise = null;
}

/**
 * Loads the shared branding logo as a base64 data URI (same approach as API
 * LetterBrandingAssets.LoadLogoDataUri used before letter PDF generation).
 */
export function preloadBrandingLogoDataUri(): Promise<string | null> {
  return loadBrandingLogoDataUri();
}

export async function loadBrandingLogoDataUri(): Promise<string | null> {
  if (brandingLogoDataUriPromise) {
    const cached = await brandingLogoDataUriPromise;
    if (cached) {
      return cached;
    }
  }

  brandingLogoDataUriPromise = fetchImageAsDataUrl(getBrandingLogoSrc())
    .then((dataUrl) => dataUrl)
    .catch(() => null);

  return brandingLogoDataUriPromise;
}

/**
 * Letter templates reference `assets/logo.png`. Point that path at the shared
 * theme logo for browser previews when the API body still has the placeholder.
 */
export function inlineCommunicationLogoPaths(html: string): string {
  if (!html) {
    return html;
  }

  return html.replace(
    /src=(["'])(?:\/)?assets\/logo\.png(?:\?[^"']*)?\1/gi,
    `src="${getBrandingLogoSrc()}"`
  );
}

/**
 * Replaces template logo paths with an inlined data URI before PDF/print capture.
 * Mirrors API LetterBrandingAssets.PrepareHtml.
 */
export function inlineBrandingLogoDataUri(
  html: string,
  logoDataUri: string | null
): string {
  if (!html) {
    return html;
  }

  if (!logoDataUri) {
    return inlineCommunicationLogoPaths(html);
  }

  return html
    .replace(
      /src=(["'])(?:\/)?assets\/logo\.png(?:\?[^"']*)?\1/gi,
      `src="${logoDataUri}"`
    )
    .replace(
      /src=(["'])\/assets\/logo\.png(?:\?[^"']*)?\1/gi,
      `src="${logoDataUri}"`
    );
}

export async function prepareHtmlWithBrandingLogo(
  html: string
): Promise<string> {
  const logoDataUri = await loadBrandingLogoDataUri();
  return inlineBrandingLogoDataUri(html, logoDataUri);
}

export async function fetchImageAsDataUrl(src: string): Promise<string> {
  const absoluteSrc = new URL(src, window.location.href).href;

  try {
    const response = await fetch(absoluteSrc, { cache: "force-cache" });
    if (!response.ok) {
      throw new Error(`Failed to load image (${response.status})`);
    }

    const blob = await response.blob();
    return blobToDataUrl(blob);
  } catch {
    return loadImageElementAsDataUrl(absoluteSrc);
  }
}

function loadImageElementAsDataUrl(src: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext("2d");
        if (!context) {
          reject(new Error("Canvas not supported"));
          return;
        }

        context.drawImage(image, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (error) {
        reject(error);
      }
    };
    image.onerror = () => reject(new Error("Failed to load image"));
    image.src = src;
  });
}

export function applyBrandingLogoToImages(
  root: ParentNode,
  logoDataUri: string | null
): void {
  if (!logoDataUri) {
    return;
  }

  root.querySelectorAll("img").forEach((node) => {
    const image = node as HTMLImageElement;
    const src = image.currentSrc || image.src || image.getAttribute("src") || "";
    const isBrandingLogo =
      image.classList.contains("document-logo") ||
      image.classList.contains("app-logo") ||
      src.includes(TEMPLATE_LOGO_PATH);

    if (!isBrandingLogo) {
      return;
    }

    image.src = logoDataUri;
    image.removeAttribute("srcset");
    image.removeAttribute("crossorigin");
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Failed to read image data"));
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("Failed to read image"));
    reader.readAsDataURL(blob);
  });
}
